import api from 'services/api';
import { array, enforce, nullable, num, numeric, object, runSchema, str } from 'validation';

import { filterConfigSchema, listColumnConfigSchema, type FilterConfig, type ListColumnConfig } from 'api/shared/listConfig';
import { folderPathSchema, type FolderPath } from 'api/shared/folderPath';
import { schedulingWindowReferenceSchema, schedulingWindowScheduleSchema, type SchedulingWindowReference, type SchedulingWindowSchedule } from './types';

// GET /scheduling_windows — the list read. Backend: routes/scheduling_windows.js
// builds its own inline query, then attaches `folder_path` per row and wraps the
// rows as `nodes` alongside pagination and the list-table config.
//
// `SchedulingWindowRow` is a SEPARATE type from the single-record
// `SchedulingWindow`, even though the columns are identical today: the list has
// its own copy of the SQL rather than calling `windowDetailsSQL()`, so the two are
// equal-by-coincidence, not identity (see ../../README.md). The nested rule and
// link element types ARE shared — those are one DB row shape either way.
export interface SchedulingWindowRow {
  readonly scheduling_window_id: number;
  readonly name: string;
  readonly description: string | null;
  readonly timezone: string;
  readonly zone_name: string | null;
  readonly created_at: string;
  readonly updated_at: string;
  readonly folder_id: number | null;
  readonly folder_path: FolderPath | null;
  readonly id: string | null;
  readonly schedules: readonly SchedulingWindowSchedule[];
  readonly references: readonly SchedulingWindowReference[];
}

interface Pagination {
  readonly total_records: number;
  readonly total_pages: number;
  readonly current_page: number;
  readonly limit: number;
  readonly offset: number;
}

/**
 * The list page. The rows arrive under `nodes` — the key this route has always
 * used, unrelated to node definitions.
 */
export interface SchedulingWindowsPage {
  readonly nodes: readonly SchedulingWindowRow[];
  readonly pagination: Pagination;
  readonly config: readonly ListColumnConfig[];
  readonly filtersConfig: readonly FilterConfig[];
}

const rowSchema = object<SchedulingWindowRow>({
  scheduling_window_id: num(),
  name: str(),
  description: nullable(str()),
  timezone: str(),
  zone_name: nullable(str()),
  created_at: str(),
  updated_at: str(),
  folder_id: nullable(num()),
  folder_path: nullable(folderPathSchema),
  id: nullable(str()),
  schedules: array(schedulingWindowScheduleSchema),
  references: array(schedulingWindowReferenceSchema),
});

// COUNT-derived totals can arrive as numeric strings from Postgres.
const paginationSchema = object<Pagination>({
  total_records: numeric(),
  total_pages: numeric(),
  current_page: numeric(),
  limit: numeric(),
  offset: numeric(),
});

const pageSchema = object<SchedulingWindowsPage>({
  nodes: array(rowSchema),
  pagination: paginationSchema,
  config: array(listColumnConfigSchema),
  filtersConfig: array(filterConfigSchema),
});

/**
 * GET `/scheduling_windows`. Returns the full list page (rows + pagination + list
 * config). A row whose numeric identity drifted is dropped; a drifted pagination
 * total fails the call, since a list cannot be paged from a number it cannot
 * trust. Throws on transport error or structural validation failure.
 */
export const getSchedulingWindows = async (params: Record<string, unknown> = {}): Promise<SchedulingWindowsPage> => {
  const res = await api.get('/scheduling_windows', { params });
  const r = runSchema(pageSchema, res.data, 'getSchedulingWindows');
  if (!r.ok) {
    throw new Error('getSchedulingWindows: response failed validation');
  }
  return enforce(r.value, r.warnings, ['nodes[*].scheduling_window_id', 'pagination.total_records', 'pagination.total_pages']);
};
